import axios from "axios";
import React, { Component } from "react";
import cookie from "react-cookies";
import { Redirect } from "react-router-dom";
import {
  Alert,
  Button,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
} from "reactstrap";
import classnames from "classnames";
import constants from "../../Constants";
import PlacesAutocomplete from "../../components/common/PlacesAutocomplete";
import { DEPARTMENTS, JOB_TITLES, normalizeOption, selectOptionsWithCurrent } from "../../constants/contactOptions";
import "../integrations/integrations.css";
import "./profile.css";

const baseURL = constants.baseURL;

const SA_PROVINCES = [
  "Eastern Cape",
  "Free State",
  "Gauteng",
  "KwaZulu-Natal",
  "Limpopo",
  "Mpumalanga",
  "Northern Cape",
  "North West",
  "Western Cape",
];

function componentText(c) {
  if (!c) return "";
  return c.longText || c.long_name || c.shortText || c.short_name || "";
}

function parseAddressComponents(components) {
  const find = (...types) => {
    for (const t of types) {
      const c = (components || []).find((x) => {
        const ts = x.types || x.type || [];
        return ts.includes(t);
      });
      if (c) return componentText(c);
    }
    return "";
  };
  return {
    city: find("locality", "administrative_area_level_2", "sublocality"),
    province: find("administrative_area_level_1"),
    postal_code: find("postal_code"),
    country: find("country"),
  };
}

function matchSaProvince(name) {
  if (!name) return "";
  const normalized = name.toLowerCase().replace(/^province of\s+/i, "").trim();
  const hit = SA_PROVINCES.find(
    (p) => p.toLowerCase() === normalized || p.toLowerCase().includes(normalized)
  );
  return hit || name;
}

const SECTIONS = [
  { id: "account", label: "Contact", icon: "mdi-account-outline" },
  { id: "business", label: "Business", icon: "mdi-domain" },
  { id: "finance", label: "Billing & finance", icon: "mdi-cash-multiple" },
  { id: "integrations", label: "Integrations", icon: "mdi-puzzle-outline" },
];

const EMPTY_BUSINESS = {
  company_legal_name: "",
  company_trading_name: "",
  company_registration_number: "",
  vat_number: "",
  company_phone: "",
  company_registered_address: "",
  company_city: "",
  company_province: "Gauteng",
  company_postal_code: "",
  company_country: "South Africa",
  website_url: "",
  finance_email: "",
  industry: "",
  contact_job_title: "",
  department: "",
  domains: "",
  integration_category: "",
  expected_monthly_volume: "",
};

const IDENTITY_DOCUMENTS = [
  {
    type: "cipc_document",
    label: "CIPC registration document",
    hint: "Company registration certificate or CIPC disclosure (PDF or image, max 10 MB).",
  },
  {
    type: "personal_id",
    label: "Personal ID",
    hint: "ID book, smart ID, or passport of the authorised signatory (PDF or image, max 10 MB).",
  },
];

const IDENTITY_ACCEPT = "application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif,.pdf,.jpg,.jpeg,.png,.webp,.heic,.heif";

function formatZar(cents) {
  const n = Number(cents) || 0;
  return "R " + (n / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function domainsToText(domains) {
  if (!domains) return "";
  if (Array.isArray(domains)) return domains.join("\n");
  return String(domains);
}

function domainsFromText(text) {
  if (!text || !String(text).trim()) return [];
  return [
    ...new Set(
      String(text)
        .split(/[\n,;]+/)
        .map((d) =>
          d
            .trim()
            .replace(/^https?:\/\//, "")
            .replace(/\/.*$/, "")
            .replace(/^www\./, "")
            .toLowerCase()
        )
        .filter(Boolean)
    ),
  ];
}

function profileSnapshot(account, business) {
  return JSON.stringify({
    account: {
      firstname: (account?.firstname || "").trim(),
      lastname: (account?.lastname || "").trim(),
      mobilenumber: (account?.mobilenumber || "").trim(),
    },
    business: {
      ...business,
      domains: domainsFromText(business?.domains),
    },
  });
}

class Profile extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeSection: "account",
      account: { customerid: "", firstname: "", lastname: "", email: "", mobilenumber: "", mobilenumberverified: false },
      business: { ...EMPTY_BUSINESS },
      billing: {},
      integrations: {},
      meta: {},
      isSubmitting: false,
      isLoading: true,
      errorMessage: "",
      successMessage: "",
      saveFeedback: null,
      savedSnapshot: "",
      redirectToDashboard: false,
      identity_documents: {},
      identityUploading: {},
      identityUploadError: "",
      team: {},
      canEditBusiness: true,
    };
    this.saveBarRef = React.createRef();
    this._saveFeedbackTimer = null;
  }

  componentWillUnmount() {
    if (this._saveFeedbackTimer) clearTimeout(this._saveFeedbackTimer);
  }

  componentDidMount() {
    document.title = "Account | QikTruck";
    const hash = (window.location.hash || "").replace("#", "");
    if (SECTIONS.some((s) => s.id === hash)) {
      this.setState({ activeSection: hash });
    }
    this.fetchProfile();
  }

  go = (id) => {
    window.location.hash = id;
    this.setState({ activeSection: id });
  };

  applyProfileData = (data) => {
    const account = {
      ...(data.account || {}),
      customerid: data.account?.customerid || cookie.load("localsessionid") || "",
    };
    const rawBusiness = data.business || {};
    const { identity_documents: identityDocs, ...businessFields } = rawBusiness;
    const business = {
      ...EMPTY_BUSINESS,
      ...businessFields,
      contact_job_title: normalizeOption(businessFields?.contact_job_title, JOB_TITLES),
      department: normalizeOption(businessFields?.department, DEPARTMENTS),
      domains: domainsToText(businessFields?.domains),
      company_province: businessFields?.company_province || "Gauteng",
      company_country: businessFields?.company_country || "South Africa",
    };
    this.setState({
      account,
      business,
      identity_documents: identityDocs || {},
      billing: data.billing || {},
      integrations: data.integrations || {},
      meta: data.meta || {},
      team: data.team || {},
      canEditBusiness: data.team?.canEditBusiness !== false,
      savedSnapshot: profileSnapshot(account, business),
    });
  };

  scrollToSaveBar = () => {
    try {
      this.saveBarRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    } catch {
      /* noop */
    }
  };

  setSaveFeedback = (saveFeedback, autoClearMs = 0) => {
    if (this._saveFeedbackTimer) clearTimeout(this._saveFeedbackTimer);
    this.setState({ saveFeedback });
    if (autoClearMs > 0) {
      this._saveFeedbackTimer = setTimeout(() => {
        this.setState({ saveFeedback: null });
      }, autoClearMs);
    }
  };

  authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: cookie.load("session-id-qt"),
  });

  customerId = () => {
    const { account } = this.state;
    return account?.customerid || cookie.load("localsessionid") || "";
  };

  fetchProfile = () => {
    const sessionId = cookie.load("session-id-qt");
    if (!sessionId) {
      this.setState({ isLoading: false, redirectToDashboard: true });
      return;
    }

    this.setState({ isLoading: true });
    axios
      .get(baseURL + "/merchant/account-profile", { headers: this.authHeaders() })
      .then((response) => {
        if (response.status === 200 && response.data) {
          this.applyProfileData(response.data);
          this.setState({ isLoading: false });
        } else {
          this.setState({ isLoading: false });
        }
      })
      .catch((error) => {
        console.log("Error fetching account profile:", error);
        this.setState({
          isLoading: false,
          errorMessage: "Failed to load account. Please try again.",
        });
      });
  };

  handleAccountChange = (event) => {
    const { name, value } = event.target;
    this.setState({ account: { ...this.state.account, [name]: value } });
  };

  handleBusinessChange = (event) => {
    const { name, value } = event.target;
    this.setState({ business: { ...this.state.business, [name]: value } });
  };

  uploadIdentityDocument = async (documentType, file) => {
    if (!file) return;
    const maxBytes = 10 * 1024 * 1024;
    if (file.size > maxBytes) {
      this.setState({ identityUploadError: "File too large. Maximum size is 10 MB." });
      return;
    }

    this.setState({
      identityUploading: { ...this.state.identityUploading, [documentType]: true },
      identityUploadError: "",
    });

    try {
      const urlResp = await axios.post(
        baseURL + "/merchant/identity-documents/upload-url",
        {
          documentType,
          fileName: file.name,
          contentType: file.type || "application/octet-stream",
          contentLength: file.size,
        },
        { headers: this.authHeaders() }
      );
      const { uploadUrl, fileKey } = urlResp.data || {};
      if (!uploadUrl || !fileKey) throw new Error("Upload could not be started.");

      await axios.put(uploadUrl, file, {
        headers: { "Content-Type": file.type || "application/octet-stream" },
      });

      const confirmResp = await axios.post(
        baseURL + "/merchant/identity-documents/confirm",
        {
          documentType,
          fileKey,
          fileName: file.name,
          contentType: file.type || null,
        },
        { headers: this.authHeaders() }
      );

      const doc = confirmResp.data?.document;
      this.setState((prev) => ({
        identity_documents: {
          ...prev.identity_documents,
          [documentType]: doc || { on_file: true, file_name: file.name },
        },
        identityUploadError: "",
      }));
      this.setSaveFeedback({ type: "success", message: "Document uploaded." }, 4000);
    } catch (error) {
      const msg =
        error?.response?.data?.message || error?.message || "Could not upload document. Please try again.";
      this.setState({ identityUploadError: msg });
    } finally {
      this.setState((prev) => ({
        identityUploading: { ...prev.identityUploading, [documentType]: false },
      }));
    }
  };

  handleIdentityFileChange = (documentType) => (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) this.uploadIdentityDocument(documentType, file);
  };

  renderIdentityDocumentUpload = (doc) => {
    const meta = this.state.identity_documents?.[doc.type] || {};
    const uploading = !!this.state.identityUploading?.[doc.type];
    const inputId = `identity-doc-${doc.type}`;

    return (
      <Col md={6} key={doc.type}>
        <FormGroup className="qt-identity-doc">
          <Label for={inputId}>{doc.label}</Label>
          <p className="qt-identity-doc-hint">{doc.hint}</p>
          {meta.on_file ? (
            <div className="qt-identity-doc-status">
              <i className="mdi mdi-check-circle text-success" />
              <span>
                On file
                {meta.file_name ? `: ${meta.file_name}` : ""}
                {meta.uploaded_at
                  ? ` · ${new Date(meta.uploaded_at).toLocaleDateString()}`
                  : ""}
              </span>
            </div>
          ) : (
            <div className="qt-identity-doc-status qt-identity-doc-status--empty">
              <i className="mdi mdi-file-document-outline" />
              <span>Not uploaded yet</span>
            </div>
          )}
          <div className="qt-identity-doc-actions">
            <Input
              id={inputId}
              type="file"
              accept={IDENTITY_ACCEPT}
              onChange={this.handleIdentityFileChange(doc.type)}
              disabled={uploading}
              className="qt-identity-doc-input"
            />
            {uploading ? (
              <span className="qt-identity-doc-uploading">
                <Spinner size="sm" /> Uploading…
              </span>
            ) : null}
          </div>
        </FormGroup>
      </Col>
    );
  };

  onBusinessAddressPlace = (place) => {
    const address = place?.formatted_address || "";
    const parsed = parseAddressComponents(place?.raw?.addressComponents);
    this.setState({
      business: {
        ...this.state.business,
        company_registered_address: address,
        company_city: parsed.city || this.state.business.company_city,
        company_province: parsed.province
          ? matchSaProvince(parsed.province)
          : this.state.business.company_province,
        company_postal_code: parsed.postal_code || this.state.business.company_postal_code,
        company_country: parsed.country || this.state.business.company_country || "South Africa",
      },
    });
  };

  validateAccount = () => {
    const { firstname, lastname, mobilenumber } = this.state.account;
    if (!firstname?.trim() || !lastname?.trim() || !mobilenumber?.trim()) {
      this.setState({ errorMessage: "First name, last name, and mobile number are required." });
      return false;
    }
    const digits = mobilenumber.replace(/[^0-9]/g, "");
    if (digits.length < 10) {
      this.setState({ errorMessage: "Enter a valid mobile number (at least 10 digits)." });
      return false;
    }
    return true;
  };

  saveProfile = (e) => {
    e.preventDefault();
    if (!this.validateAccount()) {
      this.scrollToSaveBar();
      return;
    }

    this.setState({
      isSubmitting: true,
      errorMessage: "",
      successMessage: "",
      saveFeedback: { type: "saving", message: "Saving your account…" },
    });
    this.scrollToSaveBar();

    const { account, business, canEditBusiness } = this.state;
    const payload = {
      account: {
        firstname: account.firstname,
        lastname: account.lastname,
        mobilenumber: account.mobilenumber,
      },
    };
    if (canEditBusiness !== false) {
      payload.business = {
        ...business,
        domains: domainsFromText(business.domains),
      };
    }

    axios
      .put(baseURL + "/merchant/account-profile", payload, { headers: this.authHeaders() })
      .then((response) => {
        if (response.status === 200 && response.data) {
          const acc = response.data.account || {};
          cookie.save("ctName", acc.firstname, { path: "/", maxAge: 86400, secure: true });
          this.applyProfileData(response.data);
          this.setState({
            successMessage: "Account saved successfully.",
            errorMessage: "",
          });
          this.setSaveFeedback(
            { type: "success", message: "Saved - your account details are up to date." },
            5000
          );
        } else {
          const msg = "Could not save. Please try again.";
          this.setState({ errorMessage: msg });
          this.setSaveFeedback({ type: "error", message: msg });
        }
      })
      .catch((err) => {
        const msg = err?.response?.data?.message || "Could not save. Please try again.";
        this.setState({ errorMessage: msg });
        this.setSaveFeedback({ type: "error", message: msg });
      })
      .finally(() => {
        this.setState({ isSubmitting: false });
      });
  };

  renderSaveBar() {
    const { isSubmitting, saveFeedback, account, business, savedSnapshot } = this.state;
    const isDirty = profileSnapshot(account, business) !== savedSnapshot;
    const feedback = saveFeedback || (isDirty ? { type: "dirty", message: "You have unsaved changes" } : null);

    let msgClass = "qt-account-save-bar__msg";
    let icon = null;
    let msgText = "";

    if (isSubmitting || feedback?.type === "saving") {
      msgClass += " qt-account-save-bar__msg--saving";
      icon = <Spinner size="sm" className="me-2" />;
      msgText = "Saving your account…";
    } else if (feedback?.type === "success") {
      msgClass += " qt-account-save-bar__msg--success";
      icon = <i className="mdi mdi-check-circle me-2" />;
      msgText = feedback.message;
    } else if (feedback?.type === "error") {
      msgClass += " qt-account-save-bar__msg--error";
      icon = <i className="mdi mdi-alert-circle me-2" />;
      msgText = feedback.message;
    } else if (feedback?.type === "dirty") {
      msgClass += " qt-account-save-bar__msg--dirty";
      icon = <span className="qt-settings-save-bar__dot" aria-hidden="true" />;
      msgText = feedback.message;
    }

    return (
      <div className="qt-settings-save-bar qt-account-save-bar" ref={this.saveBarRef}>
        <div className={msgClass}>
          {icon}
          <span>{msgText || "Review your details, then save."}</span>
        </div>
        <div className="qt-settings-save-bar__actions">
          <Button color="primary" type="submit" disabled={isSubmitting} className="qt-settings-save-bar__save">
            {isSubmitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Saving…
              </>
            ) : (
              <>
                <i className="mdi mdi-content-save-outline me-1" />
                Save changes
              </>
            )}
          </Button>
        </div>
      </div>
    );
  }

  renderPrefillBanner() {
    const { meta } = this.state;
    if (!meta?.prefilled_from_application) return null;
    return (
      <div className="qt-prefill-banner">
        <i className="mdi mdi-information-outline me-1" />
        Some business fields were filled from your Partner API application
        {meta.application_date ? ` (${new Date(meta.application_date).toLocaleDateString()})` : ""}.
        Review and save to keep them on your account.
      </div>
    );
  }

  renderAccountSection() {
    const { account } = this.state;
    const customerId = this.customerId();
    return (
      <div className="qt-card">
        <div className="qt-card-body">
          <h4 className="qt-section-title">Contact person</h4>
          <p className="qt-section-lead">
            Who we reach for account notices, trip updates, and integration support.
          </p>

          {customerId ? (
            <div className="qt-account-id-banner">
              <i className="mdi mdi-identifier" />
              <div>
                <span className="qt-account-id-banner__label">Customer ID</span>
                <code className="qt-account-id-banner__value">{customerId}</code>
                <span className="qt-account-id-banner__hint">
                  Quote this when contacting support or linking API registrations.
                </span>
              </div>
            </div>
          ) : null}

          <p className="qt-form-group-label">Name</p>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="firstname">First name</Label>
                <Input id="firstname" name="firstname" value={account.firstname || ""} onChange={this.handleAccountChange} />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="lastname">Last name</Label>
                <Input id="lastname" name="lastname" value={account.lastname || ""} onChange={this.handleAccountChange} />
              </FormGroup>
            </Col>
          </Row>

          <p className="qt-form-group-label">Contact details</p>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="email">Email</Label>
                <Input id="email" name="email" type="email" value={account.email || ""} readOnly />
                <small className="text-muted">
                  Change email in <a href="/settings">Settings</a>.
                </small>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="mobilenumber">Mobile</Label>
                <Input
                  id="mobilenumber"
                  name="mobilenumber"
                  type="tel"
                  value={account.mobilenumber || ""}
                  onChange={this.handleAccountChange}
                />
                {account.mobilenumberverified ? (
                  <small className="text-success d-block mt-1">
                    <i className="mdi mdi-check-circle" /> Verified
                  </small>
                ) : (
                  <small className="text-muted d-block mt-1">Not verified yet</small>
                )}
              </FormGroup>
            </Col>
          </Row>

          <p className="qt-form-group-label">Role</p>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="contact_job_title">Job title</Label>
                <Input
                  id="contact_job_title"
                  name="contact_job_title"
                  type="select"
                  value={this.state.business.contact_job_title || ""}
                  onChange={this.handleBusinessChange}
                >
                  <option value="">Select job title…</option>
                  {selectOptionsWithCurrent(JOB_TITLES, this.state.business.contact_job_title).map((title) => (
                    <option key={title} value={title}>
                      {title}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="department">Department</Label>
                <Input
                  id="department"
                  name="department"
                  type="select"
                  value={this.state.business.department || ""}
                  onChange={this.handleBusinessChange}
                >
                  <option value="">Select department…</option>
                  {selectOptionsWithCurrent(DEPARTMENTS, this.state.business.department).map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>
        </div>
      </div>
    );
  }

  renderBusinessSection() {
    const b = this.state.business;
    const readOnly = this.state.canEditBusiness === false;
    return (
      <div className="qt-card qt-card--address">
        <div className="qt-card-body">
          {readOnly ? (
            <Alert color="light" className="border mb-3">
              <i className="mdi mdi-eye-outline me-1" />
              Business profile is read-only for your role. Ask an owner or admin to make changes.
            </Alert>
          ) : null}
          <fieldset disabled={readOnly} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
          <h4 className="qt-section-title">Business identity</h4>
          <p className="qt-section-lead">
            Legal and trading details for invoices, Partner API review, and widget verification.
            This is your live account record - API applications are snapshots at submit time.
          </p>
          {this.renderPrefillBanner()}
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="company_legal_name">Legal company name</Label>
                <Input
                  id="company_legal_name"
                  name="company_legal_name"
                  value={b.company_legal_name || ""}
                  onChange={this.handleBusinessChange}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="company_trading_name">Trading name (optional)</Label>
                <Input
                  id="company_trading_name"
                  name="company_trading_name"
                  value={b.company_trading_name || ""}
                  onChange={this.handleBusinessChange}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="company_registration_number">Company registration no.</Label>
                <Input
                  id="company_registration_number"
                  name="company_registration_number"
                  value={b.company_registration_number || ""}
                  onChange={this.handleBusinessChange}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="vat_number">VAT number (optional)</Label>
                <Input id="vat_number" name="vat_number" value={b.vat_number || ""} onChange={this.handleBusinessChange} />
              </FormGroup>
            </Col>
            {IDENTITY_DOCUMENTS.map((doc) => this.renderIdentityDocumentUpload(doc))}
            {this.state.identityUploadError ? (
              <Col xs={12}>
                <Alert color="danger" className="mb-0 py-2">
                  {this.state.identityUploadError}
                </Alert>
              </Col>
            ) : null}
            <Col md={6}>
              <FormGroup>
                <Label for="company_phone">Company phone</Label>
                <Input
                  id="company_phone"
                  name="company_phone"
                  value={b.company_phone || ""}
                  onChange={this.handleBusinessChange}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="website_url">Website</Label>
                <Input
                  id="website_url"
                  name="website_url"
                  placeholder="https://"
                  value={b.website_url || ""}
                  onChange={this.handleBusinessChange}
                />
              </FormGroup>
            </Col>
            <Col xs={12}>
              <FormGroup>
                <Label for="company_registered_address">Registered address</Label>
                <PlacesAutocomplete
                  type="text"
                  className="form-control"
                  id="account-company-address"
                  placeholder="Search registered address in South Africa…"
                  defaultValue={b.company_registered_address || ""}
                  onInputChange={(val) =>
                    this.setState({
                      business: { ...this.state.business, company_registered_address: val },
                    })
                  }
                  onPlaceSelected={this.onBusinessAddressPlace}
                />
                <small className="text-muted d-block mt-1">
                  Start typing to search - city, province, and postal code fill in when you pick a result.
                </small>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label for="company_city">City</Label>
                <Input id="company_city" name="company_city" value={b.company_city || ""} onChange={this.handleBusinessChange} />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label for="company_province">Province</Label>
                <Input
                  id="company_province"
                  name="company_province"
                  type="select"
                  value={b.company_province || "Gauteng"}
                  onChange={this.handleBusinessChange}
                >
                  {SA_PROVINCES.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label for="company_postal_code">Postal code</Label>
                <Input
                  id="company_postal_code"
                  name="company_postal_code"
                  value={b.company_postal_code || ""}
                  onChange={this.handleBusinessChange}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="industry">Industry / category</Label>
                <Input id="industry" name="industry" value={b.industry || ""} onChange={this.handleBusinessChange} />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="finance_email">Finance / invoice email</Label>
                <Input
                  id="finance_email"
                  name="finance_email"
                  type="email"
                  value={b.finance_email || ""}
                  onChange={this.handleBusinessChange}
                />
              </FormGroup>
            </Col>
            <Col xs={12}>
              <FormGroup>
                <Label for="domains">Domains (one per line)</Label>
                <Input
                  id="domains"
                  name="domains"
                  type="textarea"
                  rows={3}
                  value={b.domains || ""}
                  onChange={this.handleBusinessChange}
                  placeholder="yourstore.co.za"
                />
                <small className="text-muted">Used for widget verification and Partner API domain allowlists.</small>
              </FormGroup>
            </Col>
          </Row>
          </fieldset>
        </div>
      </div>
    );
  }

  renderFinanceSection() {
    const { billing, business, integrations } = this.state;
    const outstanding = billing.outstanding_cents || 0;
    const financeEmail = billing.finance_email || business.finance_email || "";
    const payerLabel =
      billing.payment_payer === "end_customer" ? "End customer" : "Merchant account";
    const isPostpaid = billing.mode === "postpaid_invoice";
    const partnerBillingApplies =
      billing.partner_billing_applies === true ||
      integrations?.partner_api_activated === true;

    return (
      <div className="qt-finance-hub">
        {outstanding > 0 ? (
          <div className="qt-finance-alert">
            <div className="qt-finance-alert-icon">
              <i className="mdi mdi-alert-circle-outline" />
            </div>
            <div className="flex-grow-1">
              <h5>Outstanding balance</h5>
              <p>
                <strong className="qt-finance-alert-amount">{formatZar(outstanding)}</strong>
                {billing.outstanding_orders > 0 ? (
                  <span>
                    {" "}
                    across {billing.outstanding_orders} merchant-billed trip
                    {billing.outstanding_orders !== 1 ? "s" : ""}
                  </span>
                ) : null}
                . Pay via EFT on the billing page.
              </p>
            </div>
            <a href="/billing" className="btn btn-primary btn-sm qt-finance-cta">
              Pay now
            </a>
          </div>
        ) : null}

        <div className="qt-card mb-3">
          <div className="qt-card-body">
            <div className="qt-section-head">
              <div className="qt-section-icon qt-section-icon--mint">
                <i className="mdi mdi-map-marker-path" />
              </div>
              <div>
                <h4 className="qt-section-title">How billing fits together</h4>
                <p className="qt-section-desc mb-0">
                  {partnerBillingApplies
                    ? "Three places, one flow - your account details, integration settings, and trip settlement."
                    : "Settle finished portal trips via Billing. Prepaid vs postpaid only applies once Partner API or widget billing is activated."}
                </p>
              </div>
            </div>

            <Row className="g-3 qt-finance-flow">
              <Col md={4}>
                <div className="qt-flow-card">
                  <div className="qt-flow-icon qt-flow-icon--1">
                    <i className="mdi mdi-account-cog" />
                  </div>
                  <div className="qt-flow-step">Step 1</div>
                  <div className="qt-flow-title">Account (here)</div>
                  <p className="qt-flow-desc">
                    Company identity, VAT details, and where we send invoices.
                  </p>
                </div>
              </Col>
              <Col md={4}>
                <div className="qt-flow-card">
                  <div className="qt-flow-icon qt-flow-icon--2">
                    <i className="mdi mdi-tune-vertical" />
                  </div>
                  <div className="qt-flow-step">Step 2</div>
                  <div className="qt-flow-title">
                    {partnerBillingApplies ? "Integration billing" : "Optional integrations"}
                  </div>
                  <p className="qt-flow-desc">
                    {partnerBillingApplies
                      ? "Prepaid vs postpaid, default payer per trip, and billing contact."
                      : "Only for Partner API / widget. Portal bookings do not use prepaid/postpaid."}
                  </p>
                </div>
              </Col>
              <Col md={4}>
                <div className="qt-flow-card">
                  <div className="qt-flow-icon qt-flow-icon--3">
                    <i className="mdi mdi-receipt" />
                  </div>
                  <div className="qt-flow-step">Step 3</div>
                  <div className="qt-flow-title">Trip billing</div>
                  <p className="qt-flow-desc">
                    View history, select trips, and pay outstanding merchant-billed amounts.
                  </p>
                </div>
              </Col>
            </Row>
          </div>
        </div>

        <Row className="g-3 mb-3">
          <Col sm={6} lg={3}>
            <div className="qt-finance-kpi">
              <div className="qt-finance-kpi-icon qt-finance-kpi-icon--teal">
                <i className="mdi mdi-credit-card-outline" />
              </div>
              <span className="qt-finance-kpi-label">Settlement</span>
              <strong>
                {billing.mode_label ||
                  (partnerBillingApplies ? "Prepaid per order" : "Merchant account")}
              </strong>
              {partnerBillingApplies ? (
                <span className={"qt-finance-badge " + (isPostpaid ? "is-postpaid" : "is-prepaid")}>
                  {isPostpaid ? "Invoice" : "Per trip"}
                </span>
              ) : (
                <span className="qt-finance-kpi-hint">Portal trips · settle via Billing</span>
              )}
            </div>
          </Col>
          <Col sm={6} lg={3}>
            <div className="qt-finance-kpi">
              <div className="qt-finance-kpi-icon qt-finance-kpi-icon--orange">
                <i className="mdi mdi-account-cash" />
              </div>
              <span className="qt-finance-kpi-label">
                {partnerBillingApplies ? "Default payer" : "Who pays"}
              </span>
              <strong>{partnerBillingApplies ? payerLabel : "Merchant account"}</strong>
              <span className="qt-finance-kpi-hint">
                {partnerBillingApplies
                  ? "Per widget / API trip"
                  : "You settle finished trips"}
              </span>
            </div>
          </Col>
          <Col sm={6} lg={3}>
            <div className={"qt-finance-kpi" + (outstanding > 0 ? " qt-finance-kpi--alert" : "")}>
              <div className="qt-finance-kpi-icon qt-finance-kpi-icon--danger">
                <i className="mdi mdi-cash-minus" />
              </div>
              <span className="qt-finance-kpi-label">Outstanding</span>
              <strong className={outstanding > 0 ? "text-danger" : ""}>{formatZar(outstanding)}</strong>
              {billing.outstanding_orders > 0 ? (
                <span className="qt-finance-kpi-hint">{billing.outstanding_orders} trips</span>
              ) : (
                <span className="qt-finance-kpi-hint">All settled</span>
              )}
            </div>
          </Col>
          <Col sm={6} lg={3}>
            <div className="qt-finance-kpi">
              <div className="qt-finance-kpi-icon qt-finance-kpi-icon--mint">
                <i className="mdi mdi-email-outline" />
              </div>
              <span className="qt-finance-kpi-label">Invoice email</span>
              <strong className="qt-finance-kpi-email">{financeEmail || "Not set"}</strong>
              {!financeEmail ? (
                <span className="qt-finance-kpi-hint">Add under Business tab</span>
              ) : null}
            </div>
          </Col>
        </Row>

        <Row className="g-3">
          <Col md={6}>
            <a href="/billing" className="qt-link-card qt-link-card--action">
              <div className="qt-link-card-top">
                <h5>
                  <i className="mdi mdi-receipt me-1" />
                  Trip billing &amp; payment
                </h5>
                <i className="mdi mdi-arrow-right qt-link-card-arrow" />
              </div>
              <p>
                Trip history, spend charts, EFT banking details, and pay outstanding
                merchant-billed deliveries. End-customer PayFast payments never appear here.
              </p>
              {outstanding > 0 ? (
                <span className="qt-link-card-badge">Action required</span>
              ) : null}
            </a>
          </Col>
          <Col md={6}>
            {partnerBillingApplies ? (
              <a href="/integrations#partner-billing" className="qt-link-card qt-link-card--action">
                <div className="qt-link-card-top">
                  <h5>
                    <i className="mdi mdi-tune-vertical me-1" />
                    Integration billing settings
                  </h5>
                  <i className="mdi mdi-arrow-right qt-link-card-arrow" />
                </div>
                <p>
                  Choose prepaid vs postpaid invoice, default payment payer, and billing contact for
                  Partner API / widget trips.
                </p>
              </a>
            ) : (
              <a href="/billing" className="qt-link-card qt-link-card--action">
                <div className="qt-link-card-top">
                  <h5>
                    <i className="mdi mdi-bank me-1" />
                    How portal settlement works
                  </h5>
                  <i className="mdi mdi-arrow-right qt-link-card-arrow" />
                </div>
                <p>
                  Book and run trips in the portal, then settle finished merchant-billed trips via EFT
                  or PayFast. No prepaid/postpaid API mode applies until integrations are activated.
                </p>
              </a>
            )}
          </Col>
          <Col md={6}>
            <a href="/profile#business" className="qt-link-card qt-link-card--action">
              <div className="qt-link-card-top">
                <h5>
                  <i className="mdi mdi-domain me-1" />
                  Business &amp; VAT details
                </h5>
                <i className="mdi mdi-arrow-right qt-link-card-arrow" />
              </div>
              <p>Legal name, registration number, registered address, and finance invoice email.</p>
            </a>
          </Col>
          <Col md={6}>
            <a href="/support" className="qt-link-card qt-link-card--action">
              <div className="qt-link-card-top">
                <h5>
                  <i className="mdi mdi-lifebuoy me-1" />
                  Billing support
                </h5>
                <i className="mdi mdi-arrow-right qt-link-card-arrow" />
              </div>
              <p>
                Questions about invoices, payment allocation, or account fees? Our team can help.
              </p>
            </a>
          </Col>
        </Row>
      </div>
    );
  }

  renderIntegrationsSection() {
    const { integrations } = this.state;
    return (
      <Row className="g-3">
        <Col md={6}>
          <a href="/integrations" className="qt-link-card">
            <h5>
              <i className="mdi mdi-web me-1" />
              Website widget
            </h5>
            <p>Domains, embed code, warehouse pickup, and packaging defaults.</p>
          </a>
        </Col>
        <Col md={6}>
          <a href="/integrations#partner" className="qt-link-card">
            <h5>
              <i className="mdi mdi-api me-1" />
              Partner API
              {integrations.partner_api_activated ? (
                <span className="badge bg-success ms-2">Active</span>
              ) : (
                <span className="badge bg-secondary ms-2">Not activated</span>
              )}
            </h5>
            <p>
              {integrations.partner_api_activated
                ? integrations.partner_api_live_enabled
                  ? "Sandbox and production keys available in the integrations hub."
                  : "Sandbox keys available - production unlocks after QikTruck go-live."
                : "Apply for access or complete your application from the integrations hub."}
            </p>
          </a>
        </Col>
        <Col md={6}>
          <a href="/api-docs" className="qt-link-card">
            <h5>
              <i className="mdi mdi-book-open-page-variant me-1" />
              API documentation
            </h5>
            <p>Reference for quotes, orders, webhooks, and payment flows.</p>
          </a>
        </Col>
      </Row>
    );
  }

  renderSectionBody() {
    switch (this.state.activeSection) {
      case "business":
        return this.renderBusinessSection();
      case "finance":
        return this.renderFinanceSection();
      case "integrations":
        return this.renderIntegrationsSection();
      default:
        return this.renderAccountSection();
    }
  }

  render() {
    const { isLoading, isSubmitting, errorMessage, successMessage, redirectToDashboard, activeSection } =
      this.state;

    if (redirectToDashboard) {
      return <Redirect to="/dashboard" />;
    }

    const showSave = activeSection === "account" || activeSection === "business";

    return (
      <div className="qt-integrations qt-account">
        <Container>
          <div className="qt-page-head">
            <div className="qt-breadcrumb">
              <a href="/dashboard">Dashboard</a>
              <span className="mx-1">/</span>
              Account
            </div>
            <div className="qt-page-title-row">
              <div>
                <h1 className="qt-page-title">
                  <i className="mdi mdi-account-cog-outline" />
                  Account &amp; business
                </h1>
                <p className="qt-page-subtitle mb-0">
                  One place for contact details, company identity, and links to billing and integrations.
                </p>
              </div>
              {!isLoading && this.customerId() ? (
                <div className="qt-site-chip">
                  <i className="mdi mdi-identifier" />
                  Customer ID <code>{this.customerId()}</code>
                </div>
              ) : null}
            </div>
          </div>

          {errorMessage ? (
            <Alert color="danger" className="qt-alert mb-3">
              <i className="mdi mdi-alert-circle-outline me-2" />
              {errorMessage}
            </Alert>
          ) : null}
          {successMessage ? (
            <Alert color="success" className="qt-alert mb-3">
              <i className="mdi mdi-check-circle-outline me-2" />
              {successMessage}
            </Alert>
          ) : null}

          {isLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <p className="text-muted mt-3">Loading account…</p>
            </div>
          ) : (
            <Form onSubmit={this.saveProfile}>
              <fieldset disabled={isSubmitting} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
              <Row>
                <Col lg={3} className="mb-3 d-none d-lg-block">
                  <div className="qt-sidebar">
                    <div className="qt-sidebar-card">
                      <div className="qt-sidebar-head">
                        <i className="mdi mdi-account-box-outline me-1" />
                        Manage
                      </div>
                      <Nav vertical className="qt-side-nav">
                        {SECTIONS.map((sec) => (
                          <NavItem key={sec.id}>
                            <NavLink
                              className={classnames({ active: activeSection === sec.id })}
                              onClick={() => this.go(sec.id)}
                              style={{ cursor: "pointer" }}
                            >
                              <i className={"mdi " + sec.icon} />
                              {sec.label}
                            </NavLink>
                          </NavItem>
                        ))}
                      </Nav>
                    </div>
                  </div>
                </Col>
                <Col lg={9}>
                  <div className="qt-mobile-nav d-lg-none mb-3">
                    {SECTIONS.map((sec) => (
                      <button
                        key={sec.id}
                        type="button"
                        className={"qt-mobile-nav-btn" + (activeSection === sec.id ? " is-active" : "")}
                        onClick={() => this.go(sec.id)}
                      >
                        <i className={"mdi " + sec.icon} />
                        {sec.label}
                      </button>
                    ))}
                  </div>
                  <div className="qt-section-panel">{this.renderSectionBody()}</div>
                  {showSave ? this.renderSaveBar() : null}
                </Col>
              </Row>
              </fieldset>
            </Form>
          )}
        </Container>
      </div>
    );
  }
}

export default Profile;
